"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { Button } from "@/components/ui/button";
import { blockUserAction } from "@/app/actions/moderation";

type Props = {
  blockedUserId: string;
  blockedUserName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the blocked user's id after success so the caller can
   *  optimistically filter that user's content from the visible list —
   *  Apple Guideline 1.2 requires "remove from feed instantly". */
  onBlocked?: (blockedUserId: string) => void;
};

export function UserBlockConfirm({
  blockedUserId,
  blockedUserName,
  open,
  onOpenChange,
  onBlocked,
}: Props) {
  const t = useTranslations("reviews.block");
  const [submitting, setSubmitting] = useState(false);

  const handleClose = (next: boolean) => {
    if (submitting) return;
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await blockUserAction(blockedUserId);
      if ("success" in result) {
        toast.success(t("successToast"));
        onBlocked?.(blockedUserId);
        handleClose(false);
        return;
      }
      const key =
        result.error === "ALREADY_BLOCKED"
          ? "errorAlreadyBlocked"
          : result.error === "SELF_BLOCK"
            ? "errorSelfBlock"
            : result.error === "LOGIN_REQUIRED"
              ? "errorLoginRequired"
              : "errorUnknown";
      toast.error(t(key));
    } catch (error) {
      console.error("[UserBlockConfirm] block failed", error);
      toast.error(t("errorUnknown"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={handleClose}
      title={t("confirmTitle")}
      description={t("confirmDescription")}
      drawerHeight="fit"
      footer={
        <div className="flex w-full gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => handleClose(false)}
            disabled={submitting}
          >
            {t("cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="flex-1"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? t("submitting") : t("confirmButton")}
          </Button>
        </div>
      }
    >
      <ul className="space-y-1.5 py-2 text-sm text-gray-600">
        <li>• {t("effectHideReviews")}</li>
        <li>• {t("effectNoNotification")}</li>
        <li>• {t("effectCanUnblock")}</li>
      </ul>
    </ResponsiveModal>
  );
}
